import { ROUTE } from './routes'
import type { TranslationKeys } from '~/plugins/i18n'
import { DataType } from '~/types/analytics'
import {
  DataHealthCheck,
  DataHealthCheckStatus,
  DataHealthCheckType
} from '~/types/dataQuality'

export const CONFIG_BY_DATA_HEALTH_CHECK_TYPE: {
  [key in DataHealthCheckType]: {
    route: ROUTE
    icon: string
    title: TranslationKeys
    subTitle: TranslationKeys
    helpCenterLink: TranslationKeys
    unhealthyDataTitle: TranslationKeys
    unhealthyDataDescription: TranslationKeys
    messageByDataType: {
      [key in DataType]: {
        completeTotal: TranslationKeys
        incompleteTotal: TranslationKeys
      }
    }
  }
} = {
  [DataHealthCheckType.NULL_VALUES]: {
    route: ROUTE.SETTINGS__INTEGRATIONS__DATA_COMPLETENESS,
    icon: 'icon-puzzle',
    title: 'settings.integrations.dataCompleteness.title',
    subTitle: 'settings.integrations.dataCompleteness.subTitle',
    helpCenterLink: 'helpCenter.dataQuality.dataCompletenessLink',
    unhealthyDataTitle:
      'settings.integrations.dataCompleteness.unhealthyData.title',
    unhealthyDataDescription:
      'settings.integrations.dataCompleteness.unhealthyData.description',
    messageByDataType: {
      [DataType.EMPLOYEE]: {
        completeTotal:
          'settings.integrations.dataHealth.employee.dataCompleteness.completeTotal',
        incompleteTotal:
          'settings.integrations.dataHealth.employee.dataCompleteness.incompleteTotal'
      },
      [DataType.RECRUITMENT]: {
        completeTotal:
          'settings.integrations.dataHealth.recruitment.dataCompleteness.completeTotal',
        incompleteTotal:
          'settings.integrations.dataHealth.recruitment.dataCompleteness.incompleteTotal'
      }
    }
  },
  [DataHealthCheckType.INCOHERENT_VALUES]: {
    route: ROUTE.SETTINGS__INTEGRATIONS__DATA_COHERENCE,
    icon: 'icon-bug',
    title: 'settings.integrations.dataCoherence.title',
    subTitle: 'settings.integrations.dataCoherence.subTitle',
    helpCenterLink: 'helpCenter.dataQuality.dataCoherenceLink',
    unhealthyDataTitle:
      'settings.integrations.dataCoherence.unhealthyData.title',
    unhealthyDataDescription:
      'settings.integrations.dataCoherence.unhealthyData.description',
    messageByDataType: {
      [DataType.EMPLOYEE]: {
        completeTotal:
          'settings.integrations.dataHealth.employee.dataCoherence.completeTotal',
        incompleteTotal:
          'settings.integrations.dataHealth.employee.dataCoherence.incompleteTotal'
      },
      [DataType.RECRUITMENT]: {
        completeTotal:
          'settings.integrations.dataHealth.recruitment.dataCoherence.completeTotal',
        incompleteTotal:
          'settings.integrations.dataHealth.recruitment.dataCoherence.incompleteTotal'
      }
    }
  }
}

export const CONFIG_BY_DATA_HEALTH_CHECK: {
  [key in DataHealthCheck]: {
    [DataHealthCheckStatus.FAIL]: TranslationKeys
    [DataHealthCheckStatus.SUCCESS]: TranslationKeys
  }
} = {
  // Data completeness
  [DataHealthCheck.NULL_BASE_SALARY]: {
    [DataHealthCheckStatus.FAIL]:
      'settings.dataHealth.module.dataHealth.dataCompleteness.FAIL',
    [DataHealthCheckStatus.SUCCESS]:
      'settings.dataHealth.module.dataHealth.dataCompleteness.SUCCESS'
  },
  [DataHealthCheck.NULL_EMPLOYEE_ARRIVAL_DATE]: {
    [DataHealthCheckStatus.FAIL]:
      'settings.dataHealth.module.dataHealth.dataCompleteness.FAIL',
    [DataHealthCheckStatus.SUCCESS]:
      'settings.dataHealth.module.dataHealth.dataCompleteness.SUCCESS'
  },
  [DataHealthCheck.NULL_EMPLOYEE_GENDER]: {
    [DataHealthCheckStatus.FAIL]:
      'settings.dataHealth.module.dataHealth.dataCompleteness.FAIL',
    [DataHealthCheckStatus.SUCCESS]:
      'settings.dataHealth.module.dataHealth.dataCompleteness.SUCCESS'
  },
  [DataHealthCheck.NULL_EMPLOYEE_SENIORITY]: {
    [DataHealthCheckStatus.FAIL]:
      'settings.dataHealth.module.dataHealth.dataCompleteness.FAIL',
    [DataHealthCheckStatus.SUCCESS]:
      'settings.dataHealth.module.dataHealth.dataCompleteness.SUCCESS'
  },
  [DataHealthCheck.NULL_EMPLOYEE_BIRTH_DATE]: {
    [DataHealthCheckStatus.FAIL]:
      'settings.dataHealth.module.dataHealth.dataCompleteness.FAIL',
    [DataHealthCheckStatus.SUCCESS]:
      'settings.dataHealth.module.dataHealth.dataCompleteness.SUCCESS'
  },
  [DataHealthCheck.NULL_EMPLOYEE_NATIONALITY]: {
    [DataHealthCheckStatus.FAIL]:
      'settings.dataHealth.module.dataHealth.dataCompleteness.FAIL',
    [DataHealthCheckStatus.SUCCESS]:
      'settings.dataHealth.module.dataHealth.dataCompleteness.SUCCESS'
  },
  [DataHealthCheck.NULL_EMPLOYEE_CAREER_LEVEL]: {
    [DataHealthCheckStatus.FAIL]:
      'settings.dataHealth.module.dataHealth.dataCompleteness.FAIL',
    [DataHealthCheckStatus.SUCCESS]:
      'settings.dataHealth.module.dataHealth.dataCompleteness.SUCCESS'
  },
  [DataHealthCheck.NULL_EMPLOYEE_EMPLOYMENT_TYPE]: {
    [DataHealthCheckStatus.FAIL]:
      'settings.dataHealth.module.dataHealth.dataCompleteness.FAIL',
    [DataHealthCheckStatus.SUCCESS]:
      'settings.dataHealth.module.dataHealth.dataCompleteness.SUCCESS'
  },
  [DataHealthCheck.NULL_EMPLOYEE_JOB_TITLE]: {
    [DataHealthCheckStatus.FAIL]:
      'settings.dataHealth.module.dataHealth.dataCompleteness.FAIL',
    [DataHealthCheckStatus.SUCCESS]:
      'settings.dataHealth.module.dataHealth.dataCompleteness.SUCCESS'
  },
  [DataHealthCheck.NULL_EMPLOYEE_DEPARTMENT]: {
    [DataHealthCheckStatus.FAIL]:
      'settings.dataHealth.module.dataHealth.dataCompleteness.FAIL',
    [DataHealthCheckStatus.SUCCESS]:
      'settings.dataHealth.module.dataHealth.dataCompleteness.SUCCESS'
  },
  [DataHealthCheck.NULL_EMPLOYEE_GROUP_1]: {
    [DataHealthCheckStatus.FAIL]:
      'settings.dataHealth.module.dataHealth.dataCompleteness.FAIL',
    [DataHealthCheckStatus.SUCCESS]:
      'settings.dataHealth.module.dataHealth.dataCompleteness.SUCCESS'
  },
  [DataHealthCheck.NULL_EMPLOYEE_LOCATION_OFFICE]: {
    [DataHealthCheckStatus.FAIL]:
      'settings.dataHealth.module.dataHealth.dataCompleteness.FAIL',
    [DataHealthCheckStatus.SUCCESS]:
      'settings.dataHealth.module.dataHealth.dataCompleteness.SUCCESS'
  },
  // Data coherence
  [DataHealthCheck.HIGH_SALARY_RAISE]: {
    [DataHealthCheckStatus.FAIL]: `settings.dataHealth.module.dataHealth.dataCoherence.${DataHealthCheck.HIGH_SALARY_RAISE}.FAIL`,
    [DataHealthCheckStatus.SUCCESS]: `settings.dataHealth.module.dataHealth.dataCoherence.${DataHealthCheck.HIGH_SALARY_RAISE}.SUCCESS`
  },
  [DataHealthCheck.HIGH_TIME_TO_HIRE]: {
    [DataHealthCheckStatus.FAIL]: `settings.dataHealth.module.dataHealth.dataCoherence.${DataHealthCheck.HIGH_TIME_TO_HIRE}.FAIL`,
    [DataHealthCheckStatus.SUCCESS]: `settings.dataHealth.module.dataHealth.dataCoherence.${DataHealthCheck.HIGH_TIME_TO_HIRE}.SUCCESS`
  },
  [DataHealthCheck.TOO_OLD_ARRIVAL_DATE]: {
    [DataHealthCheckStatus.FAIL]: `settings.dataHealth.module.dataHealth.dataCoherence.${DataHealthCheck.TOO_OLD_ARRIVAL_DATE}.FAIL`,
    [DataHealthCheckStatus.SUCCESS]: `settings.dataHealth.module.dataHealth.dataCoherence.${DataHealthCheck.TOO_OLD_ARRIVAL_DATE}.SUCCESS`
  }
}
